import request from 'arcdynamic-request';
import { api } from '../requests/api';
import { PROPERTIES } from '../consts/tables';

const GEOCODE_DELAY = 350;
const MAX_RETRIES = 2;
const NOT_FOUND = 'not_found';

export function hasValidCoords(property) {
	const lat = property.LATITUDE && property.LATITUDE.toString() || '';
	const lng = property.LONGITUDE && property.LONGITUDE.toString() || '';
	return lat && lng && !lat.startsWith('0') && !lng.startsWith('0');
}

export function canGeocode(property) {
	return !!(property.ADDRESS && property.CITY && property.STATE);
}

export function needsGeocode(property) {
	return !hasValidCoords(property) && canGeocode(property);
}

/**
 * Where the address is, by the server's rule: a point only where something
 * confirms this address there — Google alone put Forney highway addresses in
 * Mississippi. Resolves { lat, lng, zip, city }: the ZIP to keep, and a city
 * only when the address turned out to be in another town; or null. Rejects
 * when the server can't be asked.
 */
export function geocodeAddress(property) {
	return api.post('/property-locations', {
		address: property.ADDRESS,
		city: property.CITY,
		state: property.STATE,
		zip: property.ZIP || null,
		name: property.NAME || null,
		latitude: hasValidCoords(property) ? property.LATITUDE : null,
		longitude: hasValidCoords(property) ? property.LONGITUDE : null,
	}).then(({ data }) => (data.status === NOT_FOUND ? null : { lat: data.latitude, lng: data.longitude, zip: data.zip, city: data.city }));
}

export function saveCoords(rowId, lat, lng, zip, city) {
	const row = { ARCIMEDES_ROW_ID: rowId, LATITUDE: lat, LONGITUDE: lng, ZIP: zip };
	if (city) row.CITY = city;
	return request(arc.path.api, {
		service: 'arcimedes',
		action: 'open.datasource.table.Data.updateByTableCode',
		params: [PROPERTIES, { value: [row] }],
	});
}

export function geocodeAndSave(property) {
	return geocodeAddress(property).then(geo => {
		if (geo) {
			return saveCoords(property.ARCIMEDES_ROW_ID, geo.lat, geo.lng, geo.zip, geo.city).then(() => geo);
		}
		return null;
	});
}

function delay(ms) {
	return new Promise(r => setTimeout(r, ms));
}

/**
 * Process a queue of properties needing geocoding.
 * Calls onProgress after each property is processed.
 * Retries failed items up to MAX_RETRIES times before marking as failed.
 * Returns { done, failed } counts.
 */
export function processGeocodeQueue(queue, { onProgress, shouldCancel }) {
	let done = 0;
	let failed = 0;
	const retryMap = {};

	const process = (index) => {
		if (shouldCancel()) return Promise.resolve({ done, failed });
		if (index >= queue.length) return Promise.resolve({ done, failed });

		const property = queue[index];
		const id = property.ARCIMEDES_ROW_ID;

		// Only a lookup the server could not answer is retried: "not found" is its answer, and asking again costs.
		return geocodeAndSave(property).then((geo) => ({ geo }), (error) => {
			console.error('Could not place property', id, error);
			return { error };
		}).then(({ geo, error }) => {
			if (geo) {
				property.LATITUDE = geo.lat;
				property.LONGITUDE = geo.lng;
				property.ZIP = geo.zip;
				if (geo.city) property.CITY = geo.city;
				done++;
				onProgress({ type: 'success', property, done, failed, total: queue.length });
			} else if (error && (retryMap[id] = (retryMap[id] || 0) + 1) < MAX_RETRIES) {
				queue.push(property);
			} else {
				failed++;
				onProgress({ type: 'failed', property, done, failed, total: queue.length });
			}
			return delay(GEOCODE_DELAY).then(() => process(index + 1));
		});
	};

	return process(0);
}
